import type { JSX } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Plus, Users } from 'lucide-react';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@caratuva/ui';
import { listCustomers } from '../lib/api';

/*
 * Customers admin — a unified list of the end clients an org bills: the
 * ConnectedAccounts an integrator onboards to the settlement partner ("onboarded")
 * plus the self-signup payers (global buyers the org has invoiced, "self_signup").
 * Integrator-admin only: customers never log in here. We show the KYC/KYB
 * lifecycle status plus an "Invoice-able" badge (a verified connected account
 * with a configured bank account — the flag the create-invoice flow keys off).
 * Payers never collect, so they're never invoice-able.
 */

export const Route = createFileRoute('/_app/customers/')({
  component: CustomersIndex,
});

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive';

// `status` is a plain string now — a ConnectedAccountStatus for connected
// accounts, or a buyer's KYC status for payers. Look up a badge variant and fall
// back to a neutral default for any value we don't explicitly map (the strict
// tsconfig types the lookup as `| undefined`, so a helper is required).
const STATUS_VARIANT: Record<string, BadgeVariant> = {
  created: 'outline',
  pending: 'outline',
  verifying: 'secondary',
  submitted: 'secondary',
  approved: 'default',
  verified: 'default',
  rejected: 'destructive',
};

function statusVariant(status: string): BadgeVariant {
  return STATUS_VARIANT[status] ?? 'secondary';
}

function CustomersIndex(): JSX.Element {
  const intl = useIntl();
  // Unified list: onboarded connected accounts + self-signup payers.
  const query = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => listCustomers(signal),
  });

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({ id: 'customers.title', defaultMessage: 'Customers' })}
        </h1>
        <Button asChild>
          <Link to="/customers/new">
            <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'customers.new', defaultMessage: 'New customer' })}
          </Link>
        </Button>
      </div>

      <p className="mb-4 text-sm text-navy-600">
        {intl.formatMessage({
          id: 'customers.description',
          defaultMessage:
            'End clients you onboard and verify. A customer can be invoiced once it is verified and has a configured bank account (invoice-able).',
        })}
      </p>

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({ id: 'customers.loading', defaultMessage: 'Loading customers...' })}
        </p>
      ) : query.isError ? (
        <p className="text-sm text-danger">
          {intl.formatMessage({
            id: 'customers.error',
            defaultMessage: 'Failed to load customers.',
          })}
        </p>
      ) : query.data.data.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-navy-200 bg-white px-6 py-10 text-center">
          <Users aria-hidden className="h-6 w-6 text-navy-500" strokeWidth={1.5} />
          <p className="text-sm text-navy-600">
            {intl.formatMessage({
              id: 'customers.empty',
              defaultMessage: 'No customers onboarded yet.',
            })}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/customers/new">
              <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
              {intl.formatMessage({ id: 'customers.new', defaultMessage: 'New customer' })}
            </Link>
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-navy-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {intl.formatMessage({ id: 'customers.column.name', defaultMessage: 'Name' })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({ id: 'customers.column.source', defaultMessage: 'Source' })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'customers.column.type',
                    defaultMessage: 'Type',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'customers.column.externalId',
                    defaultMessage: 'External ID',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({ id: 'customers.column.status', defaultMessage: 'Status' })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'customers.column.invoiceable',
                    defaultMessage: 'Invoice-able',
                  })}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.data.map((customer) => {
                const name = customer.name ?? customer.email ?? customer.id;
                return (
                  <TableRow key={customer.id}>
                    <TableCell>
                      {customer.kind === 'connected_account' ? (
                        <Link
                          to="/customers/$id"
                          params={{ id: customer.id }}
                          className="font-medium text-navy-800 hover:underline"
                        >
                          {name}
                        </Link>
                      ) : (
                        <Link
                          to="/customers/payer/$id"
                          params={{ id: customer.id }}
                          className="font-medium text-navy-800 hover:underline"
                        >
                          {name}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>
                      {customer.source === 'onboarded' ? (
                        <Badge variant="secondary">
                          {intl.formatMessage({
                            id: 'customers.source.onboarded',
                            defaultMessage: 'Onboarded',
                          })}
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          {intl.formatMessage({
                            id: 'customers.source.selfSignup',
                            defaultMessage: 'Self-signup',
                          })}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-navy-600 capitalize">
                      {customer.entityType}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-navy-600">
                      {customer.externalId ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(customer.status)}>{customer.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {customer.kind === 'buyer' ? (
                        <span className="text-sm text-navy-500">—</span>
                      ) : customer.invoiceable ? (
                        <Badge variant="default">
                          {intl.formatMessage({
                            id: 'customers.invoiceable.yes',
                            defaultMessage: 'Ready',
                          })}
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          {intl.formatMessage({
                            id: 'customers.invoiceable.no',
                            defaultMessage: 'Not yet',
                          })}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
